import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import type { WorkAreaSelection } from "modules/permit/components/WorkAreaSection"
import {
  buildSubactivityFieldKey,
  type CreatePermitRequestPayload,
  type PermitFieldValuePayload,
  type CreatePermitSubActivitySelection,
  type CreatePermitSubActivityFieldValue,
  type WorkerSelectionItem,
} from "modules/permit"
import {
  buildRiskAssessmentPayload,
  buildWorkAreaPayload,
  type RiskState,
} from "modules/permit/utils/permitHelpers"
import {
  GENERAL_FIELD_STATE_MAP,
  GENERAL_SECTION_CODE,
} from "modules/permit/utils/permitFieldMapping"
import type {
  TemplateConfiguration,
  TemplateField,
  TemplateSection,
} from "shared/api/templateService"
import type { WorkflowMetadata } from "modules/permit/hooks/useWorkflowStage"
import type { PermitFormState } from "../types"
import {
  buildTemplateFieldLabelKey,
  resolveTemplateFieldKeys,
} from "modules/permit/components/permit-request/utils/templateFieldUtils"

interface BuildPermitPayloadParams {
  tenantId: string
  templateConfig: TemplateConfiguration | null
  generalSection: TemplateSection | null
  permitData: PermitFormState
  extraFieldValues: Record<string, string>
  selectedActivities: string[]
  selectedWorkers: WorkerSelectionItem[]
  workAreaSelection: WorkAreaSelection | null
  riskData: RiskState
  uploadedFiles: UploadedFileItem[]
  workflowMetadata: WorkflowMetadata
  currentUser: Record<string, any> | null
  existingRequesterId?: string | null
  siteId?: string | null
}

const isGuidValue = (value: string | null | undefined) => {
  if (!value) return false
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value)
}

const normalizeGuid = (value?: string | null) => {
  if (!value || typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed || !isGuidValue(trimmed)) {
    return null
  }
  return trimmed.toUpperCase()
}

const normalizeTimeValue = (rawTime?: string | null): string | null => {
  if (!rawTime) return null
  let timeText = rawTime.trim()
  let detectedPeriod = ""

  const ampmMatch = timeText.match(/\s*(AM|PM)$/i)
  if (ampmMatch) {
    detectedPeriod = ampmMatch[1].toUpperCase()
    timeText = timeText.replace(/\s*(AM|PM)$/i, "").trim()
  }

  const [rawHours, rawMinutes = "0"] = timeText.split(":")
  let hours = Number(rawHours)
  let minutes = Number(rawMinutes)
  if (Number.isNaN(hours)) hours = 0
  if (Number.isNaN(minutes)) minutes = 0

  if (detectedPeriod === "PM" && hours < 12) {
    hours += 12
  } else if (detectedPeriod === "AM" && hours === 12) {
    hours = 0
  }

  const hh = String(Math.max(0, Math.min(23, hours))).padStart(2, "0")
  const mm = String(Math.max(0, Math.min(59, minutes))).padStart(2, "0")
  return `${hh}:${mm}`
}

const combineDateAndTime = (date: string, time: string) => {
  if (!date) return null
  const cleanDate = date.trim()
  if (!cleanDate) return null
  const cleanTime = time?.trim() || "00:00"
  return `${cleanDate}T${cleanTime}`
}

const ensureString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  return null
}

const buildFieldValuePayloadFromRaw = (
  field: TemplateField,
  sectionCode: string,
  rawValue: unknown,
  displayValue?: string,
): PermitFieldValuePayload | null => {
  if (
    rawValue === undefined ||
    rawValue === null ||
    (typeof rawValue === "string" && rawValue.trim().length === 0)
  ) {
    return null
  }

  const payload: PermitFieldValuePayload = {
    fieldId: field.fieldId,
    fieldCode: field.code ?? "",
    fieldType: field.fieldType ?? "",
    sectionCode,
  }

  const fieldType = (field.fieldType ?? "").toLowerCase()
  switch (fieldType) {
    case "date":
    case "datetime": {
      const value = ensureString(rawValue)
      if (!value) return null
      payload.valueDate = value
      break
    }
    case "time": {
      const normalized = normalizeTimeValue(ensureString(rawValue))
      if (!normalized) return null
      payload.valueText = normalized
      break
    }
    case "checkbox":
    case "boolean": {
      if (typeof rawValue === "boolean") {
        payload.valueBoolean = rawValue
      } else if (typeof rawValue === "string") {
        const normalized = rawValue.trim().toLowerCase()
        payload.valueBoolean =
          normalized === "true" || normalized === "1" || normalized === "yes"
      } else {
        return null
      }
      break
    }
    case "number":
    case "numeric": {
      const value = ensureString(rawValue)
      if (value === null) {
        return null
      }
      const parsed = Number(value)
      if (!Number.isNaN(parsed)) {
        payload.valueNumber = parsed
      } else {
        payload.valueText = value
      }
      break
    }
    default: {
      const value = ensureString(rawValue)
      if (!value) return null
      payload.valueText = value
    }
  }

  if (fieldType === "search_select" && displayValue && displayValue.trim().length > 0) {
    payload.displayValue = displayValue.trim()
  }

  return payload
}

const resolveExtraFieldValue = (
  field: TemplateField,
  extraFieldValues: Record<string, string>,
): string | undefined => {
  const keys = resolveTemplateFieldKeys(field)
  for (const key of keys) {
    if (key && extraFieldValues[key] !== undefined) {
      return extraFieldValues[key]
    }
  }
  return undefined
}

const resolveExtraFieldLabel = (
  field: TemplateField,
  extraFieldValues: Record<string, string>,
): string | undefined => {
  const keys = resolveTemplateFieldKeys(field)
  for (const key of keys) {
    if (!key) {
      continue
    }
    const value = extraFieldValues[buildTemplateFieldLabelKey(key)]
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim()
    }
  }
  return undefined
}

// Prefer values stored in extraFieldValues; fall back to mapped permitData for legacy fields.
const resolveTemplateFieldValue = (
  field: TemplateField,
  permitData: PermitFormState,
  extraFieldValues: Record<string, string>,
): string | undefined => {
  const codeKey = field.code?.toUpperCase() ?? ""
  const stateKey = codeKey ? GENERAL_FIELD_STATE_MAP[codeKey] : undefined
  if (stateKey) {
    const fieldType = (field.fieldType ?? "").toLowerCase()
    if (fieldType === "time") {
      const normalized = normalizeTimeValue((permitData as any)[stateKey])
      if (normalized) {
        return normalized
      }
      const extraValue = resolveExtraFieldValue(field, extraFieldValues)
      if (extraValue !== undefined) {
        return extraValue
      }
      const normalizedExtra = normalizeTimeValue(extraFieldValues[codeKey])
      if (normalizedExtra) {
        return normalizedExtra
      }
    }

    const raw = (permitData as any)[stateKey]
    if (raw !== null && raw !== undefined) {
      const asString = typeof raw === "string" ? raw : String(raw)
      const trimmed = asString.trim()
      if (trimmed.length > 0) {
        return trimmed
      }
    }

    const extraValue = resolveExtraFieldValue(field, extraFieldValues)
    if (extraValue !== undefined) {
      return extraValue
    }
    return undefined
  }

  const extraValue = resolveExtraFieldValue(field, extraFieldValues)
  if (extraValue !== undefined) {
    return extraValue
  }

  return undefined
}

const buildGeneralFieldValuePayload = (
  field: TemplateField,
  sectionCode: string,
  permitData: PermitFormState,
  extraFieldValues: Record<string, string>,
): PermitFieldValuePayload | null => {
  let rawValue: unknown = resolveTemplateFieldValue(field, permitData, extraFieldValues)

  const fieldType = (field.fieldType ?? "").toLowerCase()
  if (fieldType === "time") {
    if (field.code === "STTIME") {
      if (!permitData.startTime) {
        return null
      }
      rawValue = permitData.startTime
    } else if (field.code === "ENTIME") {
      if (!permitData.endTime) {
        return null
      }
      rawValue = permitData.endTime
    }
  }
  const displayValue = fieldType === "search_select" ? resolveExtraFieldLabel(field, extraFieldValues) : undefined
  return buildFieldValuePayloadFromRaw(field, sectionCode, rawValue, displayValue)
}

const buildWorkflowFieldValuePayload = (
  field: TemplateField,
  sectionCode: string,
  extraFieldValues: Record<string, string>,
): PermitFieldValuePayload | null => {
  const rawValue = resolveExtraFieldValue(field, extraFieldValues)
  const fieldType = (field.fieldType ?? "").toLowerCase()
  const displayValue = fieldType === "search_select" ? resolveExtraFieldLabel(field, extraFieldValues) : undefined
  return buildFieldValuePayloadFromRaw(field, sectionCode, rawValue, displayValue)
}

export const buildPermitPayload = ({
  tenantId,
  templateConfig,
  generalSection,
  permitData,
  extraFieldValues,
  selectedActivities,
  selectedWorkers,
  workAreaSelection,
  riskData,
  uploadedFiles,
  workflowMetadata,
  currentUser,
  existingRequesterId,
  siteId,
}: BuildPermitPayloadParams): CreatePermitRequestPayload | null => {
  if (!templateConfig || !generalSection) {
    return null
  }
  const isGeneralEnabled = templateConfig.generalEnabled !== false
  const isSubactivityEnabled = templateConfig.subactivityEnabled !== false
  const isWorkerEnabled = templateConfig.workerEnabled !== false
  const isMapEnabled = templateConfig.mapEnabled !== false
  const isRamEnabled = templateConfig.ramEnabled !== false
  const isAttachmentEnabled = templateConfig.attachmentEnabled !== false

  const generalFieldValues = isGeneralEnabled
    ? generalSection.fields
        .map((field) =>
          buildGeneralFieldValuePayload(field, generalSection.code, permitData, extraFieldValues),
        )
        .filter((value): value is PermitFieldValuePayload => value !== null)
    : []

  const workflowFieldValues: PermitFieldValuePayload[] = []

  templateConfig.sections
    ?.filter((section) => !(section.code?.toUpperCase() ?? "").startsWith("GENERAL"))
    .forEach((section) => {
      const sectionCode =
        section.code || section.templateSectionId || `${templateConfig.templateId}_WORKFLOW`
      section.fields?.forEach((field) => {
        const payload = buildWorkflowFieldValuePayload(field, sectionCode, extraFieldValues)
        if (payload) {
          workflowFieldValues.push(payload)
        }
      })
    })

  const fieldValues = [...generalFieldValues, ...workflowFieldValues]

  if (isGeneralEnabled) {
    const isotField = generalSection.fields.find(
      (field) => (field.code ?? "").toUpperCase() === "ISOT" && field.fieldId,
    )
    if (isotField) {
      const isotValue = extraFieldValues.ISOT
      if (typeof isotValue === "string" && isotValue.trim().length > 0) {
        const existingIndex = fieldValues.findIndex((value) => value.fieldId === isotField.fieldId)
        const isotPayload: PermitFieldValuePayload = {
          fieldId: isotField.fieldId,
          fieldCode: isotField.code,
          fieldType: isotField.fieldType,
          sectionCode: generalSection.code,
          valueText: isotValue,
        }
        if (existingIndex >= 0) {
          fieldValues[existingIndex] = isotPayload
        } else {
          fieldValues.push(isotPayload)
        }
      }
    }
  }

  const metadata: Record<string, unknown> = {}
  if (workflowMetadata?.workflowPermitId) {
    metadata.workflowPermitId = workflowMetadata.workflowPermitId
  }
  if (workflowMetadata?.workflowCurrentStageId) {
    metadata.workflowCurrentStageId = workflowMetadata.workflowCurrentStageId
  }
  if (workflowMetadata?.workflowCurrentStageCode) {
    metadata.workflowCurrentStageCode = workflowMetadata.workflowCurrentStageCode
  }
  if (typeof workflowMetadata?.workflowCurrentStageSequence === "number") {
    metadata.workflowCurrentStageSequence = workflowMetadata.workflowCurrentStageSequence
  }
  if (permitData.controller?.label) {
    metadata.controllerLabel = permitData.controller.label
  }
  if (permitData.requestorName) {
    metadata.requestorName = permitData.requestorName
  }
  if (permitData.requestorCompany) {
    metadata.requestorCompany = permitData.requestorCompany
  }
  if (isMapEnabled && workAreaSelection) {
    metadata.workAreaMapMode = workAreaSelection.mapMode
    if (workAreaSelection.plotPlanId) {
      metadata.workAreaPlotPlanId = workAreaSelection.plotPlanId
    }
    if (workAreaSelection.plotPlanVersionId) {
      metadata.workAreaPlotPlanVersionId = workAreaSelection.plotPlanVersionId
    }
    if (workAreaSelection.plotPlanName) {
      metadata.workAreaPlotPlanName = workAreaSelection.plotPlanName
    }
    if (workAreaSelection.plotPlanImagePath) {
      metadata.workAreaPlotPlanImagePath = workAreaSelection.plotPlanImagePath
    }
    if (workAreaSelection.marker) {
      metadata.workAreaMarker = JSON.stringify(workAreaSelection.marker)
    }
    const areas =
      workAreaSelection.areas && workAreaSelection.areas.length > 0
        ? workAreaSelection.areas
        : workAreaSelection.area
          ? [workAreaSelection.area]
          : []
    if (areas.length > 0) {
      metadata.workAreaAreas = JSON.stringify(areas)
      metadata.workAreaArea = JSON.stringify(areas[areas.length - 1])
    }
    if (workAreaSelection.gpsLatitude !== null && workAreaSelection.gpsLatitude !== undefined) {
      metadata.workAreaGpsLatitude = workAreaSelection.gpsLatitude
    }
    if (workAreaSelection.gpsLongtitude !== null && workAreaSelection.gpsLongtitude !== undefined) {
      metadata.workAreaGpsLongtitude = workAreaSelection.gpsLongtitude
    }
    if (workAreaSelection.gpsAreas && workAreaSelection.gpsAreas.length > 0) {
      metadata.workAreaGpsAreas = JSON.stringify(workAreaSelection.gpsAreas)
    }
  }

  const normalizedStartTime = normalizeTimeValue(permitData.startTime) ?? ""
  const normalizedEndTime = normalizeTimeValue(permitData.endTime) ?? ""

  const plannedStart = combineDateAndTime(permitData.startDate, normalizedStartTime)
  const plannedEnd = combineDateAndTime(permitData.endDate, normalizedEndTime)

  const supervisorEmployeeId =
    permitData.controller?.value && isGuidValue(permitData.controller.value)
      ? permitData.controller.value
      : null

  if (isGeneralEnabled && supervisorEmployeeId) {
    const controllerField = generalSection.fields?.find(
      (field) => (field.code ?? "").toUpperCase() === "CONTROLLER",
    )
    if (controllerField?.fieldId) {
      const controllerPayload: PermitFieldValuePayload = {
        fieldId: controllerField.fieldId,
        fieldCode: controllerField.code ?? "CONTROLLER",
        fieldType: controllerField.fieldType ?? "search_select",
        sectionCode: generalSection.code ?? GENERAL_SECTION_CODE,
        valueText: supervisorEmployeeId,
      }

      const existingIndex = fieldValues.findIndex((item) => item.fieldId === controllerField.fieldId)
      if (existingIndex >= 0) {
        fieldValues[existingIndex] = controllerPayload
      } else {
        fieldValues.push(controllerPayload)
      }
    }
  }

  const extractRequesterId = (user?: Record<string, unknown> | null): string | null => {
    if (!user) {
      return null
    }

    const tryCandidate = (value?: unknown): string | null => {
      if (typeof value !== 'string') {
        return null
      }
      const trimmed = value.trim()
      return isGuidValue(trimmed) ? trimmed : null
    }

    return (
      tryCandidate(user.id) ??
      tryCandidate(user.user_id) ??
      tryCandidate(user.userId) ??
      tryCandidate(user.emp_id) ??
      tryCandidate(user.empId) ??
      tryCandidate(user.employee_id) ??
      tryCandidate(user.employeeId) ??
      null
    )
  }

  const requesterId = normalizeGuid(existingRequesterId) ?? extractRequesterId(currentUser)
  const requesterCompanyId =
    normalizeGuid(currentUser?.companyId) ??
    normalizeGuid((currentUser as any)?.company_id) ??
    normalizeGuid((currentUser as any)?.companyid) ??
    null

  if (requesterCompanyId) {
    metadata.requestorCompanyId = requesterCompanyId
  }

  const workersPayload = isWorkerEnabled
    ? selectedWorkers
        .filter((worker): worker is WorkerSelectionItem => Boolean(worker?.workerId))
        .map((worker) => ({
          workerId: worker.workerId,
          companyId: worker.companyId || null,
          workerName: worker.fullName || null,
          workerPosition: worker.jobRole || null,
          roleInPermit: worker.roleInPermit ?? worker.jobRole ?? null,
        }))
    : []

  const subActivitiesPayload: CreatePermitSubActivitySelection[] = []

  if (isSubactivityEnabled) {
    selectedActivities.forEach((subactivityId) => {
      const templateSubactivity = templateConfig?.subActivities?.find(
        (item) => item.subactivityId === subactivityId,
      )

      if (!templateSubactivity) {
        return
      }

      const fieldValues: CreatePermitSubActivityFieldValue[] = []

      templateSubactivity.fields.forEach((field) => {
        const key = buildSubactivityFieldKey(subactivityId, field.subactivityFieldId)
        const rawValue = extraFieldValues[key]

        if (rawValue === undefined || rawValue === null || rawValue === "") {
          return
        }

        fieldValues.push({
          fieldId: field.subactivityFieldId,
          valueText: String(rawValue),
        })
      })

      subActivitiesPayload.push({
        subactivityId,
        fieldValues,
      })
    })
  }

  const riskPayload = isRamEnabled ? buildRiskAssessmentPayload(riskData) : null
  const workAreaPayload = isMapEnabled ? buildWorkAreaPayload(workAreaSelection) : null
  const attachmentsPayload = isAttachmentEnabled
    ? uploadedFiles
        .filter(
          (
            file,
          ): file is UploadedFileItem & {
            storagePath: string
            fileName: string
          } => Boolean(file?.storagePath && file?.fileName),
        )
        .map((file) => ({
          documentTypeId: file.docId ?? null,
          documentCode: file.documentCode ?? null,
          fileName: file.fileName,
          storagePath: file.storagePath,
          uploadedBy:
            file.uploadedBy && isGuidValue(file.uploadedBy) ? file.uploadedBy : requesterId,
          uploadedAt: file.uploadedAt ?? new Date().toISOString(),
          status: file.status ?? "ACTIVE",
        }))
    : []

  const payload: CreatePermitRequestPayload = {
    tenantId,
    templateId: templateConfig.templateId,
    templateVersionId: templateConfig.templateVersionId,
    permitTypeCode: templateConfig.permitCode,
    permitNumber: permitData.permitNo?.trim?.() || '',
    title:
      permitData.workDescription ||
      templateConfig.templateName ||
      permitData.permitType ||
      "Permit Draft",
    requesterId,
    requesterName: permitData.requestorName || currentUser?.name || null,
    requesterCompany: permitData.requestorCompany || currentUser?.company || null,
    companyId: requesterCompanyId,
    siteId: siteId ?? null,
    plannedStart,
    plannedEnd,
    supervisorEmployeeId,
    metadata: Object.keys(metadata).length > 0 ? metadata : null,
    fieldValues,
    workers: workersPayload.length > 0 ? workersPayload : undefined,
    subActivities: subActivitiesPayload.length > 0 ? subActivitiesPayload : undefined,
  }

  if (riskPayload) {
    payload.riskAssessment = riskPayload
  }
  if (workAreaPayload) {
    payload.workArea = workAreaPayload
  }
  if (attachmentsPayload.length > 0) {
    payload.attachments = attachmentsPayload
  }

  return payload
}
